import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { setLanguage, isAnalyticsReady } from "@/lib/analytics";

/**
 * Sincroniza el idioma activo de i18next con GA4.
 *
 * - Al montar, sincroniza la user_property "language" en GA4 (sin emitir language_change).
 * - Únicamente emite el evento "language_change" si el usuario cambia el idioma
 *   activamente a través del selector de la interfaz.
 *
 * Se monta una única vez en <AppContent /> (apps/web/src/App.tsx).
 */
export const useLanguageTracking = (): void => {
  const { i18n } = useTranslation();
  const initialLangRef = useRef<string | null>(null);

  useEffect(() => {
    // Capturamos el idioma inicial para no disparar el evento de cambio durante el arranque
    if (initialLangRef.current === null) {
      initialLangRef.current = i18n.language;
    }

    // Handler que se dispara con cada cambio
    const handleLangChange = (lng: string) => {
      const isUserAction = initialLangRef.current !== null && initialLangRef.current !== lng;
      setLanguage(lng, isUserAction);
      initialLangRef.current = lng;
    };

    // 1) Registrar listener de i18next
    i18n.on("languageChanged", handleLangChange);

    // 2) Sincronizar el idioma actual como user_property (sin emitir language_change)
    let attempts = 0;
    const maxAttempts = 30; // ~15 s (500 ms * 30)
    const interval = window.setInterval(() => {
      attempts += 1;
      if (isAnalyticsReady()) {
        setLanguage(i18n.language, false);
        window.clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        window.clearInterval(interval);
      }
    }, 500);

    return () => {
      i18n.off("languageChanged", handleLangChange);
      window.clearInterval(interval);
    };
  }, [i18n]);
};